import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { EmbeddedCtxSource } from "@/data/tb/embedded";
import type { AppToHost } from "@/data/tb/bridge";

/**
 * Outbound TB bridge — mirror the SPA's current route into the host
 * ThingsBoard dashboard state (DOCS-04, the `state.push` half).
 *
 * When the SPA runs inside the tb-host-widget iframe, every location
 * change posts a debounced `state.push` to the host. The widget forwards
 * it verbatim to `ctx.stateController.updateState(routeId, params, false)`
 * (controller.js `tryUpdateState` → `dispatchFromIframe 'state.push'`),
 * so the TB dashboard URL captures what the operator is looking at —
 * TB-side share links and back/forward reflect console navigation instead
 * of freezing on the widget's landing view.
 *
 * Wire shape (bridge.ts `AppToHost` "state.push", consumed verbatim by
 * the host): `{ routeId: <pathname>, params: { path: <pathname+search> } }`.
 * `params.path` round-trips the FULL in-app location (search params
 * included — time window, filters, selected keys) through TB's state
 * params, so a future inbound restore handler or TB-side action can
 * reopen the exact view.
 *
 * Deliberately does NOT instantiate `EmbeddedCtxSource`: its constructor
 * posts `ready`, which would make the host re-send ctx.init/auth.token
 * (and the adapter already owns the one live instance, privately). This
 * hook is outbound-only, so a raw postMessage — typed against the same
 * `AppToHost` contract and pinned to the same host origin the inbound
 * side uses (`?hostOrigin=` → `ems-boot-params` stash → `"*"`) — is the
 * whole job.
 *
 * Remaining DOCS-04 halves (out of scope here): applying the host
 * `timeWindow` to the SPA range (inbound sync) and "Open in ThingsBoard"
 * via `action.openEntity` on BESS detail / alarm rows.
 */

const DEBOUNCE_MS = 500;

/**
 * Resolve the host origin to pin the outbound postMessage to. Mirrors the
 * inbound pin in store/site.ts `tryBuildTbAdapter` (URL `?hostOrigin=`
 * first, then the sessionStorage boot-param stash — PERS-10 — because
 * in-app redirects strip the param from the address bar). `"*"` fallback
 * is acceptable for THIS message: it carries only the route path, no
 * credentials (unlike auth-bearing inbound traffic).
 */
function readHostOrigin(): string {
  try {
    const fromUrl = new URL(window.location.href).searchParams.get("hostOrigin");
    if (fromUrl) return fromUrl;
    const raw = sessionStorage.getItem("ems-boot-params");
    if (raw) {
      const stash: unknown = JSON.parse(raw);
      if (stash && typeof stash === "object") {
        const v = (stash as Record<string, unknown>).hostOrigin;
        if (typeof v === "string" && v) return v;
      }
    }
  } catch {
    // sandboxed storage / malformed URL — fall through to "*"
  }
  return "*";
}

// Module-level so PageShell remounts (every route change swaps the page
// component, and with it the shell) can't re-push an unchanged location.
let lastPushedPath: string | null = null;

export function useTbStatePush(): void {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!EmbeddedCtxSource.isEmbedded()) return;
    const path = pathname + search;
    if (path === lastPushedPath) return;
    // Trailing-edge debounce: rapid navigation / URL-state rewrites within
    // 500 ms collapse to one push (the cleanup cancels the stale timer).
    const timer = window.setTimeout(() => {
      lastPushedPath = path;
      const msg: AppToHost = {
        v: 1,
        type: "state.push",
        payload: { routeId: pathname, params: { path } },
      };
      try {
        window.parent.postMessage(msg, readHostOrigin());
      } catch {
        // Host gone or origin mismatch — never let bridge chatter break a page.
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [pathname, search]);
}
